import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import CreativePublicationProofViewer from "./CreativePublicationProofViewer";

import {
  buildChildrensBookPublicationOpenBookViews,
} from "../utils/childrensBookPublicationOpenBookViews";
import ChildrensBookScalablePreflightReport from "./ChildrensBookScalablePreflightReport";
import ChildrensBookManuscriptReadOnly from "./ChildrensBookManuscriptReadOnly";

import {
  buildChildrensBookPublicationProofSpec,
} from "../utils/childrensBookPublicationProofSpec";

import {
  findPublicationProofOversizedManuscriptUnits,
} from "../utils/childrensBookManuscriptCapacity";

function clean(value) {
  return String(value ?? "").trim();
}

function safeImageUrl(value) {
  const candidate = clean(value);

  if (!candidate) return "";

  try {
    const parsed = new URL(candidate);

    return (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:"
    )
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function safePreviewColour(value) {
  const candidate =
    clean(value);

  return /^#[0-9a-f]{6}$/i.test(
    candidate
  )
    ? candidate.toLowerCase()
    : "";
}

function protectedAsset(asset) {
  return Boolean(
    asset &&
    (
      asset.source === "upload" ||
      asset.storageProvider === "local"
    )
  );
}

function uniqueAssets(assets) {
  const seen = new Set();

  return assets.filter((asset) => {
    const id = clean(asset?.id);

    if (!id || seen.has(id)) {
      return false;
    }

    seen.add(id);
    return true;
  });
}


function manuscriptProofExcerpt(
  value,
  maximumWords = 90
) {
  const text = clean(value);

  if (!text) {
    return {
      text: "",
      truncated: false,
    };
  }

  const words =
    text.split(/\s+/);

  if (
    words.length <=
    maximumWords
  ) {
    return {
      text,
      truncated: false,
    };
  }

  return {
    text:
      `${words
        .slice(
          0,
          maximumWords
        )
        .join(" ")}…`,
    truncated: true,
  };
}

export default function ChildrensBookPublicationProof({
  lang = "en",
  projectPlan = {},
  projectId = "",
  childrensBookAssets = [],
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  onLoadChildrensBookAssetFile,
  onLoadCoverPalettePreview,
  onOpenPublicationReadiness,
  onOpenPublicationHandoff,
  onOpenCoverPlan,
  onOpenManuscriptPlan,
  onOpenIllustrationPlan,
  onExit,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const [
    coverPalettePreview,
    setCoverPalettePreview,
  ] = useState(null);

  /*
   * Open-Book C2B3
   *
   * Static manuscript capacity remains unchanged.
   *
   * The browser proof additionally checks the actual rendered
   * manuscript height of the physical opening currently being
   * reviewed. This prevents the UI from saying "Content ready"
   * while visible manuscript text is being clipped.
   *
   * This is an active-opening proof guard only. It is not a
   * replacement for authoritative production pagination.
   */
  const [
    activePhysicalFit,
    setActivePhysicalFit,
  ] = useState(
    () => ({
      viewLabel: "",
      overflowUnitIds:
        new Set(),
    })
  );

  const openBookViewNodeRef =
    useRef(null);

  const measureVisiblePhysicalPages =
    useCallback(
      () => {
        const root =
          openBookViewNodeRef.current;

        if (!root) {
          return;
        }

        const viewLabel =
          clean(
            root.getAttribute(
              "data-page-label"
            )
          );

        const manuscriptNodes =
          Array.from(
            root.querySelectorAll(
              "[data-physical-fit-key]"
            )
          );

        const overflowUnitIds =
          new Set();

        let measurableNodeCount =
          0;

        manuscriptNodes.forEach(
          (node) => {
            const unitId =
              clean(
                node.getAttribute(
                  "data-physical-fit-key"
                )
              );

            const clientHeight =
              Number(
                node.clientHeight
              ) || 0;

            const scrollHeight =
              Number(
                node.scrollHeight
              ) || 0;

            /*
             * A zero clientHeight means this environment has
             * not supplied usable physical layout geometry.
             *
             * Browser layout supplies real values; jsdom often
             * does not. Do not manufacture a fit decision from
             * zero geometry.
             */
            if (
              !unitId ||
              clientHeight <= 0
            ) {
              return;
            }

            measurableNodeCount +=
              1;

            if (
              scrollHeight >
              clientHeight + 1
            ) {
              overflowUnitIds.add(
                unitId
              );
            }
          }
        );

        if (
          manuscriptNodes.length >
            0 &&
          measurableNodeCount ===
            0
        ) {
          return;
        }

        setActivePhysicalFit(
          (current) => {
            const sameView =
              current.viewLabel ===
              viewLabel;

            const currentIds =
              current
                .overflowUnitIds;

            const sameSize =
              currentIds.size ===
              overflowUnitIds.size;

            const sameIds =
              sameSize &&
              Array.from(
                overflowUnitIds
              ).every(
                (unitId) =>
                  currentIds.has(
                    unitId
                  )
              );

            if (
              sameView &&
              sameIds
            ) {
              return current;
            }

            return {
              viewLabel,
              overflowUnitIds,
            };
          }
        );
      },
      []
    );

  const captureOpenBookViewNode =
    useCallback(
      (node) => {
        openBookViewNodeRef.current =
          node;

        if (!node) {
          return;
        }

        if (
          typeof window !==
            "undefined" &&
          typeof window
            .requestAnimationFrame ===
            "function"
        ) {
          window.requestAnimationFrame(
            measureVisiblePhysicalPages
          );
        } else {
          measureVisiblePhysicalPages();
        }
      },
      [
        measureVisiblePhysicalPages,
      ]
    );

  useEffect(
    () => {
      const measure =
        () =>
          measureVisiblePhysicalPages();

      if (
        typeof window !==
        "undefined"
      ) {
        window.addEventListener(
          "resize",
          measure
        );
      }

      if (
        typeof document !==
          "undefined" &&
        document.fonts?.ready &&
        typeof document
          .fonts.ready.then ===
          "function"
      ) {
        document.fonts.ready.then(
          measure
        );
      }

      return () => {
        if (
          typeof window !==
          "undefined"
        ) {
          window.removeEventListener(
            "resize",
            measure
          );
        }

        openBookViewNodeRef.current =
          null;
      };
    },
    [
      measureVisiblePhysicalPages,
    ]
  );

  const spec = useMemo(
    () => {
      const baseSpec =
        buildChildrensBookPublicationProofSpec(
          projectPlan,
          childrensBookAssets
        );

      const textRequiredUnitIds =
        new Set(
          baseSpec
            .interior
            .items
            .filter(
              (item) =>
                item.textRequired
            )
            .map(
              (item) =>
                clean(
                  item.sourceUnitId
                )
            )
            .filter(Boolean)
        );

      const oversizedUnits =
        findPublicationProofOversizedManuscriptUnits(
          projectPlan
        ).filter(
          ({
            capacity,
          }) =>
            textRequiredUnitIds.has(
              clean(
                capacity?.unitId
              )
            )
        );

      const capacitiesByUnit =
        oversizedUnits.reduce(
          (
            result,
            {
              capacity,
            }
          ) => {
            if (
              capacity?.unitId
            ) {
              result[
                capacity.unitId
              ] = capacity;
            }

            return result;
          },
          {}
        );

      const capacityIssues =
        oversizedUnits.map(
          ({
            capacity,
          }) => ({
            code:
              "oversized_manuscript_unit",
            severity:
              "blocking",
            scope:
              "interior",
            targetId:
              capacity.unitId,
            wordCount:
              capacity.wordCount,
            wordLimit:
              capacity.wordLimit,
          })
        );

      const baseIssues =
        Array.isArray(
          baseSpec.issues
        )
          ? baseSpec.issues
          : [];

      const baseBlockingIssueCount =
        Number.isFinite(
          baseSpec.readiness
            ?.blockingIssueCount
        )
          ? baseSpec.readiness
              .blockingIssueCount
          : baseIssues.filter(
              (item) =>
                item.severity ===
                "blocking"
            ).length;

      return {
        ...baseSpec,

        interior: {
          ...baseSpec.interior,

          items:
            (
              baseSpec.interior
                ?.items || []
            ).map(
              (item) => {
                const capacity =
                  capacitiesByUnit[
                    item.sourceUnitId
                  ] || null;

                return {
                  ...item,
                  capacity,
                  oversized:
                    Boolean(
                      capacity
                    ),
                  ready:
                    Boolean(
                      item.ready
                    ) &&
                    !capacity,
                };
              }
            ),
        },

        issues: [
          ...baseIssues,
          ...capacityIssues,
        ],

        readiness: {
          ...(
            baseSpec.readiness ||
            {}
          ),

          contentReady:
            Boolean(
              baseSpec.readiness
                ?.contentReady
            ) &&
            capacityIssues
              .length === 0,

          blockingIssueCount:
            baseBlockingIssueCount +
            capacityIssues.length,
        },
      };
    },
    [
      projectPlan,
      childrensBookAssets,
    ]
  );

  const previewAssets = useMemo(
    () =>
      uniqueAssets(
        [
          spec.cover.frontAsset,

          spec.cover.backImageMode !==
          "none"
            ? spec.cover.backAsset
            : null,

          spec.cover.authorPhotoAsset,

          ...spec.interior.items.map(
            (item) =>
              item.illustration
          ),
        ].filter(Boolean)
      ),
    [
      spec.cover.frontAsset,
      spec.cover.backAsset,
      spec.cover.backImageMode,
      spec.cover.authorPhotoAsset,
      spec.interior.items,
    ]
  );

  const [
    protectedPreviewUrls,
    setProtectedPreviewUrls,
  ] = useState({});

  const [
    protectedPreviewErrors,
    setProtectedPreviewErrors,
  ] = useState({});

  const [
    protectedPreviewsLoading,
    setProtectedPreviewsLoading,
  ] = useState(false);

  useEffect(() => {
    let coverPalettePreviewRequestActive =
      true;

    const id =
      clean(projectId);

    const noImageBackCover =
      spec.cover.backImageMode ===
      "none";

    const canLoadPreview =
      Boolean(
        id &&
        noImageBackCover &&
        spec.cover.frontAsset &&
        typeof onLoadCoverPalettePreview ===
          "function"
      );

    if (!canLoadPreview) {
      setCoverPalettePreview(null);

      return () => {
        coverPalettePreviewRequestActive =
          false;
      };
    }

    setCoverPalettePreview(null);

    Promise.resolve(
      onLoadCoverPalettePreview(id)
    )
      .then((response) => {
        if (
          !coverPalettePreviewRequestActive
        ) {
          return;
        }

        const payload =
          response &&
          typeof response === "object" &&
          response.data &&
          typeof response.data === "object"
            ? response.data
            : response;

        const preview =
          payload?.palettePreview;

        const background =
          safePreviewColour(
            preview?.background
          );

        const primaryText =
          safePreviewColour(
            preview?.primaryText
          );

        const secondaryText =
          safePreviewColour(
            preview?.secondaryText
          );

        if (
          payload?.ok !== true ||
          !background ||
          !primaryText ||
          !secondaryText
        ) {
          setCoverPalettePreview(null);
          return;
        }

        setCoverPalettePreview({
          background,
          primaryText,
          secondaryText,
        });
      })
      .catch(() => {
        if (
          coverPalettePreviewRequestActive
        ) {
          /*
           * Palette preview is advisory UI only.
           * Keep the deterministic neutral proof fallback
           * if the read-only preview is unavailable.
           */
          setCoverPalettePreview(null);
        }
      });

    return () => {
      coverPalettePreviewRequestActive =
        false;
    };
  }, [
    projectId,
    onLoadCoverPalettePreview,
    spec.cover.backImageMode,
    spec.cover.frontAsset,
  ]);

  const automaticBackPaletteActive =
    spec.cover.backImageMode ===
      "none" &&
    Boolean(
      coverPalettePreview
    );

  const automaticBackBackground =
    automaticBackPaletteActive
      ? coverPalettePreview.background
      : "";

  const automaticSpineBackground =
    coverPalettePreview
      ? coverPalettePreview.background
      : "";

  const automaticBackPrimaryText =
    automaticBackPaletteActive
      ? coverPalettePreview.primaryText
      : "";

  const automaticBackSecondaryText =
    automaticBackPaletteActive
      ? coverPalettePreview.secondaryText
      : "";

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    const assetsToLoad =
      previewAssets.filter(
        protectedAsset
      );

    setProtectedPreviewUrls({});
    setProtectedPreviewErrors({});

    if (
      assetsToLoad.length === 0 ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setProtectedPreviewsLoading(
        false
      );

      return () => {};
    }

    setProtectedPreviewsLoading(true);

    async function loadPreviews() {
      const nextUrls = {};
      const nextErrors = {};

      for (
        const asset of assetsToLoad
      ) {
        const id = clean(asset?.id);
        const url =
          clean(asset?.publicUrl);

        if (!id || !url) continue;

        try {
          const blob =
            await onLoadChildrensBookAssetFile(
              url
            );

          if (cancelled) return;

          if (
            typeof URL ===
              "undefined" ||
            typeof URL.createObjectURL !==
              "function"
          ) {
            throw new Error(
              "Object URL unavailable"
            );
          }

          const objectUrl =
            URL.createObjectURL(blob);

          objectUrls.push(
            objectUrl
          );

          nextUrls[id] =
            objectUrl;
        } catch {
          nextErrors[id] = isFR
            ? "Impossible de charger cette image enregistrée."
            : "Could not load this saved image.";
        }
      }

      if (!cancelled) {
        setProtectedPreviewUrls(
          nextUrls
        );

        setProtectedPreviewErrors(
          nextErrors
        );
      }
    }

    loadPreviews().finally(() => {
      if (!cancelled) {
        setProtectedPreviewsLoading(
          false
        );
      }
    });

    return () => {
      cancelled = true;

      if (
        typeof URL !==
          "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        objectUrls.forEach(
          (url) =>
            URL.revokeObjectURL(
              url
            )
        );
      }
    };
  }, [
    previewAssets,
    onLoadChildrensBookAssetFile,
    isFR,
  ]);

  function previewUrlFor(asset) {
    if (!asset) return "";

    if (protectedAsset(asset)) {
      return (
        protectedPreviewUrls[
          clean(asset.id)
        ] || ""
      );
    }

    return safeImageUrl(
      asset.publicUrl
    );
  }

  const unitLabelsById =
    useMemo(
      () =>
        spec.interior.items.reduce(
          (result, item, index) => {
            result[
              item.sourceUnitId
            ] =
              item.pageLabel ||
              item.workingTitle ||
              (
                isFR
                  ? "Page ou double page concernée"
                  : "Affected page or spread"
              );

            return result;
          },
          {}
        ),
      [
        spec.interior.items,
        isFR,
      ]
    );

  function issueTargetLabel(
    item
  ) {
    const target =
      unitLabelsById[
        clean(item?.targetId)
      ];

    return target
      ? ` — ${target}`
      : "";
  }

  function formatIssue(item) {

    if (
      item?.code ===
      "front_cover_aspect_ratio_incompatible"
    ) {
      return (
        isFR
          ? "La forme de la couverture terminée ne correspond pas au format de livre choisi. Elle serait trop rognée en production. Ajoutez une couverture préparée pour ce format."
          : "The finished front cover does not match the selected book size. It would be cropped too heavily in production. Upload a front cover prepared for this trim size."
      ) + issueTargetLabel(item);
    }


    if (
      item?.code ===
      "oversized_manuscript_unit"
    ) {
      const wordCount =
        Number(
          item.wordCount
        ) || 0;

      const wordLimit =
        Number(
          item.wordLimit
        ) || 0;

      return (
        isFR
          ? `Cette page ou double page contient ${wordCount} mots et dépasse la limite de contrôle de ${wordLimit} mots. Divisez-la avant la transmission.`
          : `This page or spread contains ${wordCount} words and exceeds the ${wordLimit}-word review limit. Divide it before handoff.`
      ) + issueTargetLabel(item);
    }

    const labels = isFR
      ? {
          missing_title:
            "Le titre du livre est manquant.",

          missing_author:
            "Le nom d’auteur est manquant.",

          missing_trim_size:
            "Les dimensions finales du livre ne sont pas définies.",

          missing_publication_format:
            "Le format de publication n’est pas défini.",

          missing_interior_units:
            "L’intérieur ne contient encore aucune page ou double page.",

          missing_front_cover:
            "L’image de première de couverture est manquante.",

          missing_back_cover_text:
            "Le texte de quatrième de couverture est manquant.",

          spine_width_requires_print_profile:
            "La largeur exacte du dos nécessite un profil d’impression.",

          missing_manuscript_text:
            "Le texte du manuscrit est manquant.",

          manuscript_not_complete:
            "Le texte du manuscrit est présent, mais l’unité n’est pas marquée comme terminée.",

          missing_required_illustration:
            "Une illustration requise est manquante.",
        }
      : {
          missing_title:
            "The book title is missing.",

          missing_author:
            "The author name is missing.",

          missing_trim_size:
            "The final trim size is not defined.",

          missing_publication_format:
            "The publication format is not defined.",

          missing_interior_units:
            "The interior does not contain any pages or spreads yet.",

          missing_front_cover:
            "The front-cover artwork is missing.",

          missing_back_cover_text:
            "The back-cover text is missing.",

          spine_width_requires_print_profile:
            "Exact spine width requires a print profile.",

          missing_manuscript_text:
            "Manuscript text is missing.",

          manuscript_not_complete:
            "Manuscript text is present, but the unit is not marked complete.",

          missing_required_illustration:
            "A required illustration is missing.",
        };

    return (
      labels[item?.code] ||
      clean(item?.code)
        .replaceAll(
          "_",
          " "
        )
    ) + issueTargetLabel(item);
  }

  function renderImage(
    asset,
    alt,
    options = {}
  ) {
    const url =
      previewUrlFor(asset);

    const assetId =
      clean(asset?.id);

    const error =
      protectedPreviewErrors[
        assetId
      ];

    if (
      protectedAsset(asset) &&
      protectedPreviewsLoading &&
      !url &&
      !error
    ) {
      return (
        <div
          aria-live="polite"
          style={{
            padding: 18,
            textAlign: "center",
            color: "#64748b",
          }}
        >
          {isFR
            ? "Chargement de l’image enregistrée…"
            : "Loading saved image…"}
        </div>
      );
    }

    if (error) {
      return (
        <div
          role="alert"
          style={{
            padding: 18,
            textAlign: "center",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      );
    }

    if (!url) {
      return (
        <div
          style={{
            minHeight:
              options.minHeight ||
              220,
            display: "grid",
            placeItems: "center",
            padding: 20,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border:
              "1px dashed #94a3b8",
            borderRadius: 12,
          }}
        >
          {options.missingLabel ||
            (
              isFR
                ? "Image non disponible"
                : "Image not available"
            )}
        </div>
      );
    }

    return (
      <img
        src={url}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          maxHeight:
            options.maxHeight ||
            620,
          objectFit:
            options.objectFit ||
            "contain",
          display: "block",
          borderRadius:
            options.borderRadius ??
            10,
        }}
      />
    );
  }


  function physicalCoverPanelAspectRatio() {
    const width =
      Number(
        spec?.format?.trim?.width
      );

    const height =
      Number(
        spec?.format?.trim?.height
      );

    if (
      spec?.format?.trim
        ?.measurable !== true ||
      !Number.isFinite(width) ||
      width <= 0 ||
      !Number.isFinite(height) ||
      height <= 0
    ) {
      return "";
    }

    return `${width} / ${height}`;
  }

  function renderFrontPanel() {
    return (
      <div
        style={{
          aspectRatio:
            physicalCoverPanelAspectRatio() ||
            undefined,
          minHeight:
            physicalCoverPanelAspectRatio()
              ? 0
              : 340,
          alignSelf: "start",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden",
          borderRadius: 12,
          background: "#f8fafc",
          border:
            "1px solid #cbd5e1",
        }}
      >
        {renderImage(
          spec.cover.frontAsset,
          isFR
            ? "Première de couverture"
            : "Front cover",
          {
            minHeight: 0,
            maxHeight: "none",
            objectFit: "cover",
            borderRadius: 0,
            missingLabel: isFR
              ? "Ajoutez l’image de première de couverture."
              : "Add the front-cover artwork.",
          }
        )}

        {spec.cover.frontPanel
          ?.textOverlayAllowed ? (
          <div
            style={{
              position: "absolute",
              inset:
                "auto 0 0 0",
              padding:
                "30px 20px 20px",
              color: "#ffffff",
              background:
                "linear-gradient(transparent, rgba(15,23,42,0.88))",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize:
                  "clamp(22px, 4vw, 38px)",
                lineHeight: 1.08,
              }}
            >
              {spec.cover.title ||
                spec.identity.title ||
                (
                  isFR
                    ? "Titre non renseigné"
                    : "Title not provided"
                )}
            </h3>

            {spec.cover.subtitle ? (
              <p
                style={{
                  margin:
                    "8px 0 0",
                  fontSize: 16,
                }}
              >
                {spec.cover.subtitle}
              </p>
            ) : null}

            <p
              style={{
                margin:
                  "12px 0 0",
                fontWeight: 800,
              }}
            >
              {spec.cover.author ||
                spec.identity.author}
            </p>
          </div>
        ) : null}
      </div>
    );
  }

  function renderCover() {
    const activeBackCoverAsset =
      spec.cover.backImageMode !==
      "none"
        ? spec.cover.backAsset
        : null;

    const backCoverImageUrl =
      previewUrlFor(
        activeBackCoverAsset
      );

    const backCoverPreviewError =
      activeBackCoverAsset
        ? protectedPreviewErrors[
            clean(
              activeBackCoverAsset.id
            )
          ] || ""
        : "";

    const authorPhotoUrl =
      previewUrlFor(
        spec.cover.authorPhotoAsset
      );

    const authorPhotoPreviewError =
      spec.cover.authorPhotoAsset
        ? protectedPreviewErrors[
            clean(
              spec.cover.authorPhotoAsset.id
            )
          ] || ""
        : "";

    if (
      spec.cover.mode !==
      "wraparound"
    ) {
      return (
        <div
          style={{
            width:
              "min(100%, 560px)",
            margin: "0 auto",
          }}
        >
          {renderFrontPanel()}
        </div>
      );
    }

    return (
      <div>
        <div
          style={{
            marginBottom: 12,
            fontSize: 13,
            fontWeight: 800,
            color: "#475569",
            textAlign: "center",
          }}
        >
          {isFR
            ? "Quatrième de couverture · Dos · Première de couverture"
            : "Back cover · Spine · Front cover"}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(260px, 1fr) minmax(54px, 0.16fr) minmax(260px, 1fr)",
            minWidth: 650,
            gap: 8,
            alignItems: "stretch",
          }}
        >
          <section
            aria-label={
              isFR
                ? "Quatrième de couverture"
                : "Back cover"
            }
            style={{
              aspectRatio:
                physicalCoverPanelAspectRatio() ||
                undefined,
              minHeight:
                physicalCoverPanelAspectRatio()
                  ? 0
                  : 340,
              alignSelf: "start",
              boxSizing: "border-box",
              padding: 24,
              borderRadius: 12,
              background:
                automaticBackBackground ||
                "#f8fafc",
              border:
                "1px solid #cbd5e1",
              display: "grid",
              gridTemplateRows:
                "auto minmax(0, 1fr) auto",
              gap: 18,
            }}
          >
            <div
              role="group"
              aria-label={
                isFR
                  ? "Zone de titre de quatrième de couverture"
                  : "Back-cover title area"
              }
              style={{
                textAlign: "center",
                padding: "2px 8px 0",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontFamily:
                    spec.cover.backTitleFontFamily ||
                    undefined,
                  fontSize:
                    spec.cover.backTitleFontSize
                      ? `${spec.cover.backTitleFontSize}pt`
                      : 19,
                  lineHeight:
                    spec.cover.backTitleLineSpacing ||
                    1.22,
                  textAlign:
                    spec.cover.backTitleAlignment &&
                    spec.cover.backTitleAlignment !==
                      "auto"
                      ? spec.cover.backTitleAlignment
                      : "center",
                  letterSpacing:
                    "0.01em",
                  color:
                    spec.cover.backTitleColour ||
                    automaticBackPrimaryText ||
                    "#1e293b",
                  visibility:
                    spec.cover.backTitleMode ===
                    "hide"
                      ? "hidden"
                      : "visible",
                }}
              >
                {spec.cover.title ||
                  spec.identity.title}
              </h3>
            </div>

            <div
              role="group"
              aria-label={
                isFR
                  ? "Zone du texte de quatrième de couverture"
                  : "Back-cover blurb area"
              }
              style={{
                width: "100%",
                maxWidth: "48ch",
                margin: "0 auto",
                whiteSpace: "pre-wrap",
                fontFamily:
                  spec.cover.backBlurbFontFamily ||
                  undefined,
                fontSize:
                  spec.cover.backBlurbFontSize
                    ? `${spec.cover.backBlurbFontSize}pt`
                    : 14,
                lineHeight:
                  spec.cover.backBlurbLineSpacing ||
                  1.58,
                textAlign:
                  spec.cover.backBlurbAlignment &&
                  spec.cover.backBlurbAlignment !==
                    "auto"
                    ? spec.cover.backBlurbAlignment
                    : "left",
                color:
                  spec.cover.backBlurbColour ||
                  automaticBackSecondaryText ||
                  "#334155",
                alignSelf: "start",
              }}
            >
              {spec.cover
                .backCoverText ||
                (
                  isFR
                    ? "Le texte de quatrième de couverture n’a pas encore été fourni."
                    : "Back-cover text has not been provided yet."
                )}
            </div>

            <div
              role="group"
              aria-label={
                isFR
                  ? "Composition inférieure de quatrième de couverture"
                  : "Back-cover lower composition"
              }
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "flex-end",
                gap: 12,
                minHeight: 92,
              }}
            >
              <div
                style={{
                  minWidth: 0,
                  display: "grid",
                  gap: 7,
                  alignContent: "end",
                  justifyItems: "start",
                }}
              >
                {spec.cover.authorPhotoAsset ? (
                  <div>
                    {protectedPreviewsLoading &&
                    !authorPhotoUrl &&
                    !authorPhotoPreviewError ? (
                      <div
                        role="status"
                        style={{
                          padding: "6px 0",
                          textAlign: "left",
                          color: "#475569",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {isFR
                          ? "Chargement de la photo d’auteur…"
                          : "Loading author photo…"}
                      </div>
                    ) : null}

                    {authorPhotoPreviewError ? (
                      <div
                        role="alert"
                        style={{
                          padding: "6px 0",
                          color: "#b91c1c",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {authorPhotoPreviewError}
                      </div>
                    ) : null}

                    {authorPhotoUrl ? (
                      <img
                        src={
                          authorPhotoUrl
                        }
                        alt={
                          isFR
                            ? "Photo d’auteur"
                            : "Author photo"
                        }
                        style={{
                          display: "block",
                          width: "100%",
                          maxWidth: 76,
                          maxHeight: 100,
                          objectFit:
                            "contain",
                          margin: 0,
                          borderRadius: 6,
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}

                {activeBackCoverAsset ? (
                  <div>
                    {protectedPreviewsLoading &&
                    !backCoverImageUrl &&
                    !backCoverPreviewError ? (
                      <div
                        role="status"
                        style={{
                          padding: "6px 0",
                          textAlign: "left",
                          color: "#475569",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {isFR
                          ? "Chargement de l’image de quatrième de couverture…"
                          : "Loading back-cover image…"}
                      </div>
                    ) : null}

                    {backCoverPreviewError ? (
                      <div
                        role="alert"
                        style={{
                          padding: "6px 0",
                          color: "#b91c1c",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {backCoverPreviewError}
                      </div>
                    ) : null}

                    {backCoverImageUrl ? (
                      <img
                        src={
                          backCoverImageUrl
                        }
                        alt={
                          isFR
                            ? "Image de quatrième de couverture"
                            : "Back-cover image"
                        }
                        style={{
                          display: "block",
                          width: "100%",
                          maxWidth: 76,
                          maxHeight: 100,
                          objectFit:
                            "contain",
                          margin: 0,
                          borderRadius: 6,
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}

                {
                  spec.cover.backImageRole !==
                    "author_photo" &&
                  (
                    spec.cover
                      .illustratorCredit ||
                    spec.cover
                      .contributorCredits
                  ) ? (

                  <div
                    style={{
                      maxWidth: 112,
                      fontSize: 10,
                      color: "#64748b",
                      lineHeight: 1.35,
                    }}
                  >
                    {spec.cover
                      .illustratorCredit ? (
                      <div>
                        {isFR
                          ? "Illustration : "
                          : "Illustration: "}
                        {
                          spec.cover
                            .illustratorCredit
                        }
                      </div>
                    ) : null}

                    {spec.cover
                      .contributorCredits ? (
                      <div>
                        {
                          spec.cover
                            .contributorCredits
                        }
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div
                role="region"
                aria-label={
                  isFR
                    ? "Zone réservée ISBN et code-barres"
                    : "Reserved ISBN and barcode area"
                }
                style={{
                  width: 118,
                  height: 72,
                  flex: "0 0 118px",
                  margin: "0 0 0 auto",
                  border:
                    "1px dashed #94a3b8",
                  borderRadius: 6,
                  display: "grid",
                  placeItems: "center",
                  padding: 8,
                  background: "#ffffff",
                  color: "#64748b",
                  fontSize: 11,
                  fontWeight: 800,
                  lineHeight: 1.25,
                  textAlign: "center",
                }}
              >
                {isFR
                  ? "Zone ISBN / code-barres"
                  : "ISBN / Barcode area"}
              </div>
            </div>
          </section>

          <section
            aria-label={
              isFR
                ? "Dos du livre"
                : "Book spine"
            }
            style={{
              minHeight:
                physicalCoverPanelAspectRatio()
                  ? 0
                  : 340,
              alignSelf: "stretch",
              boxSizing: "border-box",
              borderRadius: 10,
              background:
                automaticSpineBackground ||
                "#e2e8f0",
              border:
                "1px solid #94a3b8",
              display: "grid",
              placeItems: "center",
              padding: "16px 8px",
            }}
          >
            <div
              style={{
                writingMode:
                  "vertical-rl",
                fontWeight: 900,
                textAlign: "center",
                letterSpacing:
                  "0.03em",
              }}
            >
              {spec.cover.spine
                ?.eligibleForText
                ? (
                    spec.cover.spineText ||
                    spec.identity.title
                  )
                : null}
            </div>
          </section>

          {renderFrontPanel()}
        </div>

        <p
          style={{
            margin:
              "12px 0 0",
            textAlign: "center",
            fontSize: 13,
            color: "#92400e",
          }}
        >
          {
            spec.cover.spine
              ?.eligibleForText
              ? (
                  isFR
                    ? "Le dos est représenté à titre de contrôle. Sa largeur finale dépendra du profil d’impression."
                    : "The spine is shown for proofing. Its final width will depend on the print profile."
                )
              : (
                  isFR
                    ? `Le texte du dos est omis dans cette épreuve car le nombre estimé de pages est inférieur à ${spec.cover.spine?.minimumPageCountForText || 80}. La largeur finale du dos dépendra toujours du profil d’impression.`
                    : `Spine text is omitted in this proof because the estimated page count is below ${spec.cover.spine?.minimumPageCountForText || 80}. Final spine width will still depend on the print profile.`
                )
          }
        </p>
      </div>
    );
  }

  function renderInteriorItem(
    item,
    index
  ) {
    const imageUrl =
      previewUrlFor(
        item.illustration
      );

    const hasImage =
      Boolean(imageUrl);

    const label =
      item.pageLabel &&
      item.workingTitle
        ? `${item.pageLabel} · ${item.workingTitle}`
        : (
            item.pageLabel ||
            item.workingTitle ||
            (
              isFR
                ? "Page ou double page"
                : "Page or spread"
            )
          );

    const capacity =
      item.capacity || null;

    const oversized =
      Boolean(
        item.oversized
      );

    const physicalFitKey =
      clean(
        item?.sourceUnitId
      ) ||
      clean(
        item?.id
      );

    const physicalOverflow =
      Boolean(
        physicalFitKey &&
        activePhysicalFit
          .overflowUnitIds
          .has(
            physicalFitKey
          )
      );

    const proofExcerpt =
      oversized
        ? manuscriptProofExcerpt(
            item.manuscriptText
          )
        : {
            text:
              item.manuscriptText,
            truncated: false,
          };

    const unitType =
      clean(
        item.unitType
      ).toLowerCase();

    const illustrationPlan =
      clean(
        item.illustrationRequirement
      ).toLowerCase();

    const illustrationPageSide =
      clean(
        item.illustrationPageSide
      ).toLowerCase();

    const isFrontMatter =
      unitType ===
      "front_matter";

    const isSpread =
      Number(
        item.pageSpan
      ) === 2;

    const isSinglePage =
      !isSpread;

    const singleSmallIllustration =
      isSinglePage &&
      illustrationPlan ===
        "small_illustration";

    const singleHalfIllustration =
      isSinglePage &&
      illustrationPlan ===
        "half_page";

    const singleFullIllustration =
      isSinglePage &&
      illustrationPlan ===
        "full_page";

    const spreadFullPageIllustration =
      isSpread &&
      illustrationPlan ===
        "full_page" &&
      (
        illustrationPageSide ===
          "left" ||
        illustrationPageSide ===
          "right"
      );

    const quietNoTextPage =
      !item.textRequired &&
      !item.requiresIllustration;

    const layoutMode =
      isFrontMatter
        ? "front-matter"
        : spreadFullPageIllustration
          ? "spread-full-page"
          : isSpread
            ? "spread-shared"
            : singleSmallIllustration
              ? "single-shared-small"
              : singleHalfIllustration
                ? "single-shared-half"
                : singleFullIllustration
                  ? "single-full-page"
                  : item.requiresIllustration
                    ? "single-illustrated"
                    : "single-text";

    const compositionColumns =
      isSpread &&
      illustrationPlan ===
        "full_spread"
        /*
         * C1 deliberately leaves explicit Full-spread
         * composition unchanged. Its cross-gutter
         * rendering will be handled separately.
         */
        ? "minmax(0, 1.15fr) minmax(260px, 0.85fr)"
        : isSpread &&
          (
            hasImage ||
            item.requiresIllustration
          )
          ? "repeat(2, minmax(0, 1fr))"
          : "1fr";

    const compositionRows =
      isSpread
        ? "1fr"
        : singleSmallIllustration
          ? "25% minmax(0, 1fr)"
          : singleHalfIllustration
            ? "50% minmax(0, 1fr)"
            : singleFullIllustration
              ? (
                  item.textRequired
                    ? "50% minmax(0, 1fr)"
                    : "1fr"
                )
              : item.requiresIllustration
                ? "25% minmax(0, 1fr)"
                : "1fr";

    const illustrationOrder =
      spreadFullPageIllustration &&
      illustrationPageSide ===
        "right"
        ? 2
        : 1;

    const manuscriptOrder =
      spreadFullPageIllustration &&
      illustrationPageSide ===
        "right"
        ? 1
        : 2;

    const hideManuscriptRegion =
      singleFullIllustration &&
      !item.textRequired;

    /*
     * Open-Book C2B1
     *
     * Writer-facing Publication Proof is now a physical
     * open-book presentation. Ordinary Single Pages should
     * therefore use the same bounded content-surface language
     * as ordinary facing-page compositions.
     *
     * Front matter keeps its established unboxed treatment.
     */
    const singlePhysicalPageSurface =
      isSinglePage &&
      !isFrontMatter;

    const unboxedManuscript =
      isFrontMatter;

    const pageContainedIllustration =
      (
        isSpread &&
        illustrationPlan !==
          "full_spread"
      ) ||
      singlePhysicalPageSurface;

    const centredManuscriptRegion =
      isFrontMatter ||
      quietNoTextPage;

    return (
      <article
        data-testid="publication-proof-interior-item"
        data-unit-type={unitType}
        data-illustration-plan={illustrationPlan}
        data-layout-mode={layoutMode}
        style={{
          height: "100%",
          minHeight: 0,
        }}
      >
        <div
          data-testid="publication-proof-interior-composition"
          style={{
            display: "grid",
            height: "100%",
            minHeight: 0,
            gridTemplateColumns:
              compositionColumns,
            gridTemplateRows:
              compositionRows,
            gap:
              isSinglePage
                ? 16
                : 22,
            alignItems:
              spreadFullPageIllustration
                ? "stretch"
                : "start",
          }}
        >
          {hasImage ||
          item.requiresIllustration ? (
            <div
              data-testid="publication-proof-illustration-region"
              data-page-contained={
                pageContainedIllustration
                  ? "true"
                  : "false"
              }
              style={{
                order:
                  illustrationOrder,
                minWidth: 0,
                minHeight: 0,
                height: "100%",
                overflow: "hidden",
                boxSizing:
                  "border-box",
                padding:
                  pageContainedIllustration
                    ? "clamp(10px, 1.5vw, 18px)"
                    : 0,
                border:
                  pageContainedIllustration
                    ? "1px solid #cbd5e1"
                    : "none",
                borderRadius:
                  pageContainedIllustration
                    ? 10
                    : 0,
                background:
                  "#ffffff",
              }}
            >
              {renderImage(
                item.illustration,
                label,
                {
                  minHeight: 0,
                  maxHeight:
                    "100%",
                  objectFit:
                    "contain",
                  missingLabel: isFR
                    ? "L’illustration requise est manquante."
                    : "The required illustration is missing.",
                }
              )}
            </div>
          ) : null}

          {!hideManuscriptRegion ? (
          <div
            data-testid="publication-proof-manuscript-region"
            data-physical-fit-key={
              physicalFitKey || ""
            }
            data-physical-overflow={
              physicalOverflow
                ? "true"
                : "false"
            }
            data-front-matter={
              isFrontMatter
                ? "true"
                : "false"
            }
            style={{
              order:
                manuscriptOrder,
              minHeight: 0,
              height: "100%",
              maxHeight:
                "100%",
              overflowY:
                oversized ||
                !isFrontMatter
                  ? "auto"
                  : "hidden",
              overflowX:
                "hidden",
              width: "100%",
              maxWidth:
                isSpread
                  ? "none"
                  : 520,
              margin:
                isSpread
                  ? 0
                  : "0 auto",
              boxSizing:
                "border-box",
              padding:
                unboxedManuscript
                  ? "12px 8px"
                  : singlePhysicalPageSurface
                    ? "clamp(10px, 1.5vw, 18px)"
                    : "clamp(18px, 3vw, 30px)",
              background:
                "#ffffff",
              border:
                unboxedManuscript
                  ? "none"
                  : oversized
                    ? "1px solid rgba(234,88,12,0.36)"
                    : "1px solid #cbd5e1",
              borderRadius:
                unboxedManuscript
                  ? 0
                  : singlePhysicalPageSurface
                    ? 10
                    : 12,
              whiteSpace:
                "pre-wrap",
              lineHeight: 1.75,
              color: "#1e293b",
              display:
                centredManuscriptRegion
                  ? "flex"
                  : "block",
              flexDirection:
                centredManuscriptRegion
                  ? "column"
                  : undefined,
              justifyContent:
                centredManuscriptRegion
                  ? "center"
                  : undefined,
            }}
          >
            {oversized ? (
              <div
                role="alert"
                style={{
                  marginBottom: 16,
                  padding: 12,
                  borderRadius: 10,
                  color: "#9a3412",
                  background:
                    "#fff7ed",
                  border:
                    "1px solid rgba(234,88,12,0.28)",
                  whiteSpace:
                    "normal",
                  lineHeight: 1.55,
                }}
              >
                <strong>
                  {isFR
                    ? "Cette page ou double page doit être divisée."
                    : "This page or spread needs to be divided."}
                </strong>

                <div
                  style={{
                    marginTop: 5,
                  }}
                >
                  {isFR
                    ? `Cette ${item.pageSpan === 2 ? "double page" : "page simple"} contient ${capacity.wordCount} mots et dépasse la limite de contrôle de ${capacity.wordLimit} mots.`
                    : `This ${item.pageSpan === 2 ? "Two-page Spread" : "Single Page"} contains ${capacity.wordCount} words and exceeds the ${capacity.wordLimit}-word review limit.`}
                </div>
              </div>
            ) : null}

            {!item.textRequired ? (
              <div
                data-testid="publication-proof-no-text"
                style={{
                  padding: 0,
                  borderRadius: 0,
                  background:
                    "transparent",
                  border:
                    "none",
                  color:
                    "#64748b",
                  fontWeight: 700,
                  textAlign:
                    "center",
                  fontSize: 13,
                  letterSpacing:
                    "0.02em",
                }}
              >
                {isFR
                  ? "Aucun texte requis"
                  : "No text required"}
              </div>
            ) : oversized ? (
              <div
                data-testid="publication-proof-plain-text-excerpt"
                style={{
                  whiteSpace:
                    "pre-wrap",
                }}
              >
                {proofExcerpt.text ||
                  (
                    isFR
                      ? "Le texte du manuscrit est manquant."
                      : "The manuscript text is missing."
                  )}
              </div>
            ) : (
              <ChildrensBookManuscriptReadOnly
                lang={lang}
                presentation="publication_proof"
                content={
                  item.manuscriptContent
                }
                fallbackText={
                  item.manuscriptText ||
                  (
                    isFR
                      ? "Le texte du manuscrit est manquant."
                      : "The manuscript text is missing."
                  )
                }
              />
            )}

            {oversized &&
            proofExcerpt.truncated ? (
              <p
                style={{
                  margin:
                    "16px 0 0",
                  paddingTop: 12,
                  borderTop:
                    "1px solid #fed7aa",
                  color: "#9a3412",
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace:
                    "normal",
                  lineHeight: 1.5,
                }}
              >
                {isFR
                  ? "Extrait de contrôle uniquement. Le manuscrit complet reste disponible dans le Plan du manuscrit."
                  : "Preview excerpt only. The complete manuscript remains in Manuscript Plan."}
              </p>
            ) : null}
          </div>
          ) : null}
        </div>
      </article>
    );
  }


  const openBookViews =
    buildChildrensBookPublicationOpenBookViews(
      spec.interior.items,
      spec.interior.estimatedPageCount
    ).map(
      (view) => {
        const viewItems = [
          view.left?.item,
          view.right?.item,
        ].filter(Boolean);

        const uniqueViewItems =
          viewItems.filter(
            (
              item,
              index
            ) =>
              viewItems.indexOf(
                item
              ) === index
          );

        const fitMatchesView =
          clean(
            activePhysicalFit
              .viewLabel
          ) ===
          clean(
            view?.pageLabel
          );

        const viewHasPhysicalOverflow =
          fitMatchesView &&
          uniqueViewItems.some(
            (item) => {
              const unitId =
                clean(
                  item?.sourceUnitId
                ) ||
                clean(
                  item?.id
                );

              return Boolean(
                unitId &&
                activePhysicalFit
                  .overflowUnitIds
                  .has(
                    unitId
                  )
              );
            }
          );

        return {
          ...view,

          /*
           * Writer-facing Publication Proof always
           * represents an open physical book.
           *
           * Structural Single Page / Two-Page Spread
           * authority remains on the source items.
           */
          pageSpan: 2,

          ready:
            uniqueViewItems.every(
              (item) =>
                Boolean(
                  item?.ready
                )
            ) &&
            !viewHasPhysicalOverflow,

          oversized:
            uniqueViewItems.some(
              (item) =>
                Boolean(
                  item?.oversized
                )
            ),
        };
      }
    );


  function renderOpenBookPhysicalPage(
    page,
    side
  ) {
    const pageNumber =
      Number(
        page?.number
      ) || null;

    const blank =
      Boolean(
        page?.blank
      );

    return (
      <section
        data-testid={
          `publication-proof-open-book-${side}-page`
        }
        data-page-number={
          pageNumber || ""
        }
        data-blank={
          blank
            ? "true"
            : "false"
        }
        aria-label={
          pageNumber
            ? (
                isFR
                  ? `Page ${pageNumber}`
                  : `Page ${pageNumber}`
              )
            : (
                isFR
                  ? "Page blanche"
                  : "Blank page"
              )
        }
        style={{
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          boxSizing:
            "border-box",
          paddingLeft:
            !blank &&
            side === "right"
              ? 10
              : 0,
          paddingRight:
            !blank &&
            side === "left"
              ? 10
              : 0,
          position:
            "relative",
          overflow:
            "hidden",
          background:
            "#ffffff",
          borderRight:
            side === "left"
              ? "1px solid #cbd5e1"
              : "none",
          borderLeft:
            side === "right"
              ? "1px solid #cbd5e1"
              : "none",
          boxShadow:
            side === "left"
              ? "inset -12px 0 18px -20px rgba(15,23,42,0.55)"
              : "inset 12px 0 18px -20px rgba(15,23,42,0.55)",
        }}
      >
        {!blank &&
        page?.item
          ? renderInteriorItem(
              page.item,
              pageNumber || 0
            )
          : null}
      </section>
    );
  }


  function renderOpenBookView(
    view
  ) {
    const left =
      view?.left || null;

    const right =
      view?.right || null;

    const deliberateSpread =
      Boolean(
        left?.item &&
        right?.item &&
        left.item ===
          right.item &&
        left.role ===
          "spread-left" &&
        right.role ===
          "spread-right"
      );

    return (
      <div
        key={
          view?.pageLabel ||
          ""
        }
        ref={
          captureOpenBookViewNode
        }
        data-testid="publication-proof-open-book-view"
        data-page-label={
          view?.pageLabel || ""
        }
        style={{
          height: "100%",
          minHeight: 0,
          width: "100%",
          minWidth: 0,
          position:
            "relative",
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          overflow:
            "hidden",
          background:
            "#f8fafc",
        }}
      >
        {deliberateSpread ? (
          <div
            data-testid="publication-proof-open-book-spread"
            style={{
              gridColumn:
                "1 / -1",
              height: "100%",
              minHeight: 0,
              minWidth: 0,
              position:
                "relative",
              overflow:
                "hidden",
              background:
                "#ffffff",
            }}
          >
            {renderInteriorItem(
              left.item,
              view.pageStart || 0
            )}
          </div>
        ) : (
          <>
            {renderOpenBookPhysicalPage(
              left,
              "left"
            )}

            {renderOpenBookPhysicalPage(
              right,
              "right"
            )}
          </>
        )}

        <div
          data-testid="publication-proof-open-book-gutter"
          aria-hidden="true"
          style={{
            position:
              "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: 2,
            transform:
              "translateX(-1px)",
            background:
              "#94a3b8",
            boxShadow:
              "0 0 14px rgba(15,23,42,0.24)",
            pointerEvents:
              "none",
            zIndex: 5,
          }}
        />
      </div>
    );
  }


  const proofTitle =
    spec.identity.title
      ? (
          isFR
            ? `Épreuve de publication — ${spec.identity.title}`
            : `Publication Proof — ${spec.identity.title}`
        )
      : (
          isFR
            ? "Épreuve de publication"
            : "Publication Proof"
        );

  return (
    <>
      {childrensBookAssetsLoading ? (
        <div
          aria-live="polite"
          className="ns-card"
          style={{
            padding: 14,
            marginBottom: 16,
          }}
        >
          {isFR
            ? "Chargement des ressources du livre…"
            : "Loading book assets…"}
        </div>
      ) : null}

      {childrensBookAssetsError ? (
        <div
          role="alert"
          className="ns-card"
          style={{
            padding: 14,
            marginBottom: 16,
            color: "#991b1b",
          }}
        >
          {childrensBookAssetsError}
        </div>
      ) : null}

      <CreativePublicationProofViewer
        lang={lang}
        title={proofTitle}
        format={spec.format}
        bookPageGeometry
        openBookMode
        cover={spec.cover}
        interiorItems={
          openBookViews
        }
        issues={spec.issues}
        renderCover={renderCover}
        renderInteriorItem={
          renderOpenBookView
        }
        formatIssue={formatIssue}
        renderPreflightReport={() => (
          <ChildrensBookScalablePreflightReport
            lang={lang}
            issues={spec.issues}
            interiorItems={
              spec.interior.items
            }
            formatIssue={formatIssue}
            onOpenCoverPlan={
              onOpenCoverPlan
            }
            onOpenManuscriptPlan={
              onOpenManuscriptPlan
            }
            onOpenIllustrationPlan={
              onOpenIllustrationPlan
            }
          />
        )}
        onReturn={
          onOpenPublicationReadiness
        }
        onExit={onExit}
        returnLabel={
          isFR
            ? "Retour à la préparation à la publication"
            : "Return to Publication Readiness"
        }
      />

      {typeof onOpenPublicationHandoff ===
      "function" ? (
        <section
          className="ns-card ns-nbb-major-panel"
          aria-label={
            isFR
              ? "Étape suivante de publication"
              : "Next publication step"
          }
          style={{
            padding: 18,
            borderRadius: 16,
            marginTop: 18,
            border:
              spec.readiness.contentReady
                ? "1px solid rgba(22,101,52,0.24)"
                : "1px solid rgba(185,28,28,0.24)",
            background:
              spec.readiness.contentReady
                ? "#f0fdf4"
                : "#fef2f2",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3
                className="ns-card-title"
                style={{
                  margin: "0 0 6px",
                }}
              >
                {isFR
                  ? "Continuer après vérification"
                  : "Continue After Proof Review"}
              </h3>

              <p
                className="ns-card-meta"
                style={{
                  margin: 0,
                  lineHeight: 1.55,
                  maxWidth: 700,
                }}
              >
                {spec.readiness.contentReady
                  ? (
                      isFR
                        ? "Aucun problème bloquant ne subsiste. Vous pouvez préparer la transmission extérieure."
                        : "No blocking issues remain. You may prepare the external handoff."
                    )
                  : (
                      isFR
                        ? `${spec.readiness.blockingIssueCount} problème(s) bloquant(s) doivent être corrigés avant la transmission.`
                        : `${spec.readiness.blockingIssueCount} blocking issue(s) must be corrected before handoff.`
                    )}
              </p>
            </div>

            <button
              className="ns-button ns-button-primary"
              type="button"
              disabled={
                !spec.readiness.contentReady
              }
              style={{
                opacity:
                  spec.readiness.contentReady
                    ? 1
                    : 0.62,

                cursor:
                  spec.readiness.contentReady
                    ? "pointer"
                    : "not-allowed",

                background:
                  spec.readiness.contentReady
                    ? undefined
                    : "#94a3b8",

                borderColor:
                  spec.readiness.contentReady
                    ? undefined
                    : "#94a3b8",
              }}
              onClick={
                onOpenPublicationHandoff
              }
            >
              {isFR
                ? "Continuer vers la transmission pour publication"
                : "Continue to Publication Handoff"}
            </button>
          </div>
        </section>
      ) : null}

      <section
        className="ns-card"
        aria-label={
          isFR
            ? "Corriger l’épreuve"
            : "Correct the proof"
        }
        style={{
          padding: 16,
          borderRadius: 16,
          marginTop: 18,
        }}
      >
        <h3
          className="ns-card-title"
          style={{
            margin:
              "0 0 7px",
          }}
        >
          {isFR
            ? "Corriger le contenu source"
            : "Correct the Source Content"}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin:
              "0 0 14px",
            lineHeight: 1.55,
          }}
        >
          {isFR
            ? "L’épreuve est dérivée des éléments enregistrés. Corrigez-les dans leur espace d’origine, puis revenez ici."
            : "The proof is derived from saved project material. Correct it in its original workspace, then return here."}
        </p>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {typeof onOpenCoverPlan ===
          "function" ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                onOpenCoverPlan
              }
            >
              {isFR
                ? "Corriger la couverture"
                : "Correct Cover"}
            </button>
          ) : null}

          {typeof onOpenManuscriptPlan ===
          "function" ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                onOpenManuscriptPlan
              }
            >
              {isFR
                ? "Corriger le manuscrit"
                : "Correct Manuscript"}
            </button>
          ) : null}

          {typeof onOpenIllustrationPlan ===
          "function" ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                onOpenIllustrationPlan
              }
            >
              {isFR
                ? "Corriger les illustrations"
                : "Correct Illustrations"}
            </button>
          ) : null}
        </div>
      </section>
    </>
  );
}
